import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { DateTime, Interval } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { BaseStyle, BaseStyleEnums, DatePicker, DatePickerEnums, Select, SelectTypes } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { SortingState } from '@tanstack/react-table';

import { ApiResponseNHL } from '~/core/api';
import CommonMessages from '~/core/commonMessages';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';

import { useSeasonsQuery, useStandingsQuery } from './api';
import PlayoffInfoSection from './components/PlayoffInfoSection/PlayoffInfoSection';
import StandingsLegend from './components/StandingsLegend/StandingsLegend';
import StandingsTabBar from './components/StandingsTabBar/StandingsTabBar';
import { STANDINGS_INITIAL_SORT } from './consts';
import { StandingsViewType } from './enums';
import { getStandingsSections } from './standingsSectionsDefinitions';
import { Standings as StandingsProp, StandingsSeason } from './types';
import {
    getClosestRegularSeasonDate,
    getDefaultStandingsViewTypeForSeason,
    getProperNavigateUrl,
    getSeasonFromDate,
    getSeasonOptionFromSeason,
    isCurrentSeason,
    isDateInRegularSeason,
    isValidViewForSeason,
} from './utils';

import {
    Header,
    HeaderPickersTogglerPromoWrapper,
    HeaderPickersTogglerWrapper,
    HeaderPickersWrapper,
    PickersWrapper,
    SeasonSelectWrapper,
    StandingsUpdateMessage,
    ToggleWrapper,
} from './styles';

const isSmallScreen: boolean = window.innerWidth < BaseStyleEnums.ScreenWidthBreakpoints.medium;

const Standings: FC = () => {
    const [sortingState, setSortingState] = useState<SortingState>(STANDINGS_INITIAL_SORT);
    const intl = useIntl();
    const { date: dateStringFromUrl, view: viewTypeFromUrl } = useParams();
    const navigate = useNavigate();

    const [hasHydratedParams, setHasHydratedParams] = useState<boolean>(false);
    const [selectedSeason, setSelectedSeason] = useState<StandingsSeason>();

    const {
        data: { seasons: availableSeasons = [] },
        isLoading: isSeasonsResponseLoading,
        isInitialLoad: isSeasonsInitialLoad,
    }: ApiResponseNHL<{ seasons: Array<StandingsSeason> }> = useSeasonsQuery();

    const {
        data: standingsData,
        isLoading: isStandingsResponseLoading,
        isInitialLoad: isStandingsInitialLoad,
        resolvedDate,
    }: ApiResponseNHL<StandingsProp> = useStandingsQuery(dateStringFromUrl);
    const { standings, wildCardIndicator } = standingsData;

    const seasonOptions: Array<SelectTypes.Option<StandingsSeason>> = useMemo(
        () => availableSeasons.map((season) => getSeasonOptionFromSeason(season)).reverse(),
        [availableSeasons]
    );

    const selectedSeasonOption: SelectTypes.Option<StandingsSeason> | undefined = useMemo(
        () => (selectedSeason ? getSeasonOptionFromSeason(selectedSeason) : undefined),
        [selectedSeason]
    );

    const standingsViewType: StandingsViewType | undefined =
        StandingsViewType[viewTypeFromUrl as keyof StandingsViewType];

    const standingsSections: Array<ReactNode> | null = useMemo(() => {
        if (selectedSeason) {
            // standings initializes to empty object, breaking certain truthy check logic
            const standingsParam = !standings?.length ? null : standings;
            return getStandingsSections(
                standingsViewType,
                standingsParam,
                selectedSeason,
                sortingState,
                setSortingState
            );
        }

        return null;
    }, [standingsViewType, standings, selectedSeason, sortingState]);

    const currentSeason: StandingsSeason | undefined = useMemo(
        () => (availableSeasons ? availableSeasons[availableSeasons.length - 1] : undefined),
        [availableSeasons]
    );

    const earliestGameDateInSelectableSeasons = seasonOptions[seasonOptions.length - 1]?.standingsStart;

    const earliestSelectableCalendarDate = earliestGameDateInSelectableSeasons
        ? DateTime.fromISO(earliestGameDateInSelectableSeasons)
        : undefined;

    const getLatestSelectableCalendarDate = (seasons: Array<SelectTypes.Option<StandingsSeason>>): DateTime => {
        const latestSelectableSeason = seasons[0];
        const isLatestAvailableSeasonInFuture = latestSelectableSeason?.standingsStart
            ? DateTime.fromISO(latestSelectableSeason?.standingsStart) > DateTime.now()
            : false;

        // if future season is available to select in dropdown, allow user to select its standingsStart in calendar
        if (isLatestAvailableSeasonInFuture) {
            return DateTime.fromISO(latestSelectableSeason.standingsStart);
        }

        // otherwise, use today's date as the latest selectable date
        return DateTime.now();
    };

    const latestSelectableCalendarDate: DateTime | undefined = useMemo(
        () => getLatestSelectableCalendarDate(seasonOptions),
        [seasonOptions]
    );

    const { standingsEnd, standingsStart } = selectedSeason || { standingsEnd: undefined, standingsStart: undefined };

    const earliestSelectableConsoleDate = standingsStart ? DateTime.fromISO(standingsStart) : undefined;
    const formattedRegularSeasonEnd = standingsEnd ? DateTime.fromISO(standingsEnd) : undefined;

    // ensures user cannot select later than today
    const latestSelectableConsoleDate =
        formattedRegularSeasonEnd !== undefined && formattedRegularSeasonEnd > DateTime.now()
            ? DateTime.now()
            : formattedRegularSeasonEnd;

    useEffect(() => {
        if (availableSeasons.length === 0 || Object.keys(standingsData).length === 0 || hasHydratedParams) {
            return;
        }

        if (resolvedDate) {
            const seasonFromResolvedDate: SelectTypes.Option<StandingsSeason> | undefined = getSeasonFromDate(
                resolvedDate,
                availableSeasons
            );

            if (seasonFromResolvedDate) {
                setSelectedSeason(seasonFromResolvedDate);

                const isSeasonCurrentSeason = isCurrentSeason(seasonFromResolvedDate, currentSeason);

                // if we're about to set the season from resolved date, we don't actually have an appropriate
                // view type for the computed season yet; so we must compute it for the resolved season/date before navigating
                let viewType = StandingsViewType[viewTypeFromUrl as keyof StandingsViewType];

                if (viewType === undefined || !isValidViewForSeason(viewType, seasonFromResolvedDate)) {
                    viewType = getDefaultStandingsViewTypeForSeason(
                        isSeasonCurrentSeason,
                        seasonFromResolvedDate,
                        wildCardIndicator
                    );
                }

                const { standingsStart, standingsEnd }: { standingsStart: string; standingsEnd: string } =
                    seasonFromResolvedDate;

                const closestDate = getClosestRegularSeasonDate(resolvedDate, standingsStart, standingsEnd);
                const formattedClosestDate = closestDate.toISODate();

                if (formattedClosestDate) {
                    setHasHydratedParams(true);
                    const navigateUrl = getProperNavigateUrl(
                        formattedClosestDate,
                        seasonFromResolvedDate,
                        viewType,
                        isSeasonCurrentSeason,
                        wildCardIndicator
                    );
                    navigate(navigateUrl, { replace: true });
                }
            }
            return;
        }
    }, [
        availableSeasons,
        currentSeason,
        hasHydratedParams,
        navigate,
        resolvedDate,
        standingsData,
        wildCardIndicator,
        viewTypeFromUrl,
    ]);

    const onSelectDate = (date: DateTime) => {
        const formattedDate = date.toISODate();
        if (formattedDate && standingsViewType) {
            const navigateUrl = getProperNavigateUrl(
                formattedDate,
                selectedSeason,
                standingsViewType,
                isCurrentSeason(selectedSeason, currentSeason),
                wildCardIndicator
            );
            navigate(navigateUrl);
        }
    };

    const adjustSelectedDateByOffset = (offset: number) => {
        if (resolvedDate !== undefined && standingsViewType) {
            const updatedDate = resolvedDate.plus({ days: offset });
            const formattedDate = updatedDate.toISODate();

            if (formattedDate) {
                const navigateUrl = getProperNavigateUrl(
                    formattedDate,
                    selectedSeason,
                    standingsViewType,
                    isCurrentSeason(selectedSeason, currentSeason),
                    wildCardIndicator
                );
                navigate(navigateUrl);
            }
        }
    };

    const onArrowClick = (isLeft: boolean) => {
        adjustSelectedDateByOffset(isLeft ? -1 : 1);
    };

    const seasonChangeHandler = (event: unknown, season: SelectTypes.Option<StandingsSeason>) => {
        if (standingsViewType) {
            const isSeasonInFuture = DateTime.fromISO(season.standingsStart) > DateTime.now();
            const seasonNavigateDate = isSeasonInFuture ? season.standingsStart : season.standingsEnd;
            let viewType = standingsViewType;

            const isSeasonCurrentSeason = isCurrentSeason(season, currentSeason);

            if (!isValidViewForSeason(viewType, season)) {
                viewType = getDefaultStandingsViewTypeForSeason(isSeasonCurrentSeason, season, wildCardIndicator);
            }

            setSelectedSeason(season);

            const navigateUrl = getProperNavigateUrl(
                seasonNavigateDate,
                season,
                viewType,
                isSeasonCurrentSeason,
                wildCardIndicator
            );
            navigate(navigateUrl);
        }
    };

    const getIsOptionEqualToValue = <T,>(option: SelectTypes.Option<T>, value: SelectTypes.Option<T>): boolean =>
        option.label === value.label;

    // return of true means month tile will be disabled (how calendar API expects it)
    const shouldDisableCalendarMonth = (date: DateTime | undefined): boolean => {
        if (!date) {
            return true;
        }

        const dateSeason = getSeasonFromDate(date, availableSeasons);

        if (!dateSeason) {
            return true;
        }

        const { standingsStart, standingsEnd } = dateSeason;

        // Individual tiles are months - checking if any dates in the range of the month are in the regular season
        const firstDayOfMonth = date.startOf('month');
        const lastDayOfMonth = date.endOf('month');
        const monthInterval = Interval.fromDateTimes(firstDayOfMonth, lastDayOfMonth);

        // make special case to enable standingsStart date (and corresponding month tile) of future seasons
        if (monthInterval.contains(DateTime.fromISO(standingsStart))) {
            return false;
        }

        const seasonStandingsRange = Interval.fromDateTimes(
            DateTime.fromISO(standingsStart),
            DateTime.fromISO(standingsEnd)
        );

        // if we know that at least the beginning or end of the tile month is in the range of viewable standings dates, enable tile
        if (seasonStandingsRange.contains(firstDayOfMonth) || seasonStandingsRange.contains(lastDayOfMonth)) {
            return false;
        }

        // otherwise, if either the beginning or end of the viewable standings date range fall within the month, enable tile
        return !(
            monthInterval.contains(DateTime.fromISO(standingsStart)) ||
            monthInterval.contains(DateTime.fromISO(standingsEnd))
        );
    };

    // return of true means date tile will be disabled (how calendar API expects it)
    const shouldDisableCalendarDate = (date: DateTime) => {
        const dateSeason = getSeasonFromDate(date, availableSeasons);

        if (!dateSeason) {
            return true;
        }

        const { standingsStart, standingsEnd } = dateSeason;

        // make special case to enable standingsStart date tile for future seasons
        const isSeasonInFuture = DateTime.fromISO(standingsStart) > DateTime.now();
        const isTileStandingsStartDate = DateTime.fromISO(standingsStart).hasSame(date, 'day');
        if (isSeasonInFuture && isTileStandingsStartDate) {
            return false;
        }
        return !isDateInRegularSeason(date, standingsStart, standingsEnd);
    };

    const selectAriaLabels = useMemo(
        () => ({
            dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.season.dropdownButton),
            optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.season.optionsBox),
            selectLabel: intl.formatMessage(CommonMessages.tableHeaders.long.season),
        }),
        [intl]
    );

    return (
        <PageWrapper defaultPageLayout hasExternalBackground theme={BaseStyle.LightTheme}>
            {isSeasonsInitialLoad || isStandingsInitialLoad ? (
                <LoadingDisplay isLoading />
            ) : (
                <LoadingDisplay isLoading={isStandingsResponseLoading || isSeasonsResponseLoading}>
                    <>
                        <HeaderPickersTogglerPromoWrapper>
                            <HeaderPickersTogglerWrapper>
                                <HeaderPickersWrapper>
                                    <Header>
                                        <FormattedMessage
                                            defaultMessage="Standings"
                                            description="Header for Standings page"
                                        />
                                        {window.__AD_SETTINGS__.presbyDivId && (
                                            <AdTag divId={window.__AD_SETTINGS__.presbyDivId} adType={AdType.PRESBY} />
                                        )}
                                    </Header>
                                    <PickersWrapper>
                                        <SeasonSelectWrapper>
                                            <Select
                                                aria-label={selectAriaLabels.selectLabel}
                                                dropdownButtonAriaLabel={selectAriaLabels.dropdownButton}
                                                isOptionEqualToValue={getIsOptionEqualToValue}
                                                maxDropdownHeight={350}
                                                onOptionChange={seasonChangeHandler}
                                                options={seasonOptions}
                                                optionsBoxAriaLabel={selectAriaLabels.optionsBox}
                                                selectedOption={selectedSeasonOption || null}
                                            />
                                        </SeasonSelectWrapper>
                                        <DatePicker
                                            calendarMaxDate={latestSelectableCalendarDate}
                                            calendarMinDate={earliestSelectableCalendarDate}
                                            consoleMaxDate={latestSelectableConsoleDate}
                                            consoleMinDate={earliestSelectableConsoleDate}
                                            intl={intl}
                                            localeCode={window.__SITE_SETTINGS__.localeCode}
                                            minDetail={DatePickerEnums.CalendarViewType.month}
                                            onArrowClick={onArrowClick}
                                            onSelectDate={onSelectDate}
                                            selectedDate={resolvedDate}
                                            shouldDisableDate={shouldDisableCalendarDate}
                                            shouldDisableMonth={shouldDisableCalendarMonth}
                                        />
                                    </PickersWrapper>
                                </HeaderPickersWrapper>
                                <ToggleWrapper>
                                    {selectedSeason && standingsViewType && (
                                        <StandingsTabBar
                                            season={selectedSeason}
                                            selectedViewType={standingsViewType}
                                            wildcardIndicator={wildCardIndicator}
                                        />
                                    )}
                                </ToggleWrapper>
                            </HeaderPickersTogglerWrapper>
                            {window.__AD_SETTINGS__.mrecDivId && (
                                <AdTag divId={window.__AD_SETTINGS__.mrecDivId} adType={AdType.MEDIUM_RECTANGLE} />
                            )}
                        </HeaderPickersTogglerPromoWrapper>
                        {standings?.length && selectedSeason ? (
                            <div
                                id={`${standingsViewType}-tabpanel`}
                                role="tabpanel"
                                aria-labelledby={standingsViewType}
                            >
                                {standingsSections}
                            </div>
                        ) : (
                            <h1>
                                <FormattedMessage
                                    defaultMessage="No standings for this date."
                                    description="Error-handling message letting user know there are no standings for given date."
                                />
                            </h1>
                        )}
                        {standingsData.standingsDateTimeUtc !== undefined && (
                            <StandingsUpdateMessage>
                                <FormattedMessage
                                    defaultMessage="Standings update after each game ends and are current as of {date}"
                                    description="Message indicating when standings were last updated."
                                    values={{
                                        date: DateTime.fromISO(standingsData.standingsDateTimeUtc).toLocaleString({
                                            ...DateTime.DATETIME_FULL,
                                            month: 'short',
                                            year: undefined,
                                        }),
                                    }}
                                />
                            </StandingsUpdateMessage>
                        )}
                        {!isSmallScreen && <StandingsLegend standings={standings} />}
                    </>
                    <PlayoffInfoSection />
                </LoadingDisplay>
            )}
        </PageWrapper>
    );
};

export { Standings as Component };
